import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  Box, Button, Typography, Tabs, Tab, Table, TableHead, TableBody,
  TableRow, TableCell, TextField, Alert, IconButton, CircularProgress,
  Select, MenuItem as MuiMenuItem, FormControl
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { saveCarteJson } from '../utils/api';
import BackupManager from '../components/BackupManager';

const CATEGORIES = ['entrées', 'plats', 'garniture', 'sushis', 'desserts', 'boisson'];

const navStyle = { display: 'flex', gap: 1, mb: 3, borderBottom: '1px solid #eee', pb: 2 };

const DashboardPlats = () => {
  const navigate = useNavigate();
  const [user] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('user')); } catch { return null; }
  });
  const [carte, setCarte] = useState(null);
  const [selectedCat, setSelectedCat] = useState('entrées');
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!user) { navigate('/dashboard'); return; }
    const url = `/assets/json/carte.json?v=${Date.now()}`;
    console.log('[DashboardPlats] Chargement items depuis :', url);
    fetch(url)
      .then(r => { console.log('[DashboardPlats] Réponse HTTP :', r.status, r.url); return r.json(); })
      .then(data => { console.log('[DashboardPlats] Items chargés :', data); setCarte(data); })
      .catch(err => { console.error('[DashboardPlats] Erreur chargement :', err); setMessage({ type: 'error', text: 'Erreur chargement carte.json' }); });
  }, [user, navigate]);

  const handleFieldChange = (idx, field, value) => {
    setCarte(prev => {
      const cats = { ...prev.categories };
      const list = [...(cats[selectedCat] || [])];
      list[idx] = { ...list[idx], [field]: field === 'prix' ? parseFloat(value) || 0 : value };
      return { ...prev, categories: { ...cats, [selectedCat]: list } };
    });
  };

  const handleAdd = () => {
    setCarte(prev => {
      const cats = { ...prev.categories };
      const list = [...(cats[selectedCat] || [])];
      const maxId = list.reduce((m, item) => Math.max(m, item.id || 0), 0);
      list.push({ id: maxId + 1, ref: '', nom: '', prix: 0, description: '', 'sous-categ': '', image: '', precision: '' });
      return { ...prev, categories: { ...cats, [selectedCat]: list } };
    });
  };

  const handleDelete = (idx) => {
    setCarte(prev => {
      const cats = { ...prev.categories };
      const list = (cats[selectedCat] || []).filter((_, i) => i !== idx);
      return { ...prev, categories: { ...cats, [selectedCat]: list } };
    });
  };

  const reloadCarte = () => {
    fetch(`/assets/json/carte.json?v=${Date.now()}`)
      .then(r => r.json())
      .then(data => setCarte(data));
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await saveCarteJson(carte, user);
      setMessage({ type: 'success', text: 'Carte sauvegardée avec succès !' });
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Erreur lors de la sauvegarde' });
    }
    setSaving(false);
  };

  if (!user) return null;
  if (!carte) return <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>;

  const items = carte.categories[selectedCat] || [];
  const subCatOptions = [...new Set(items.map(i => i['sous-categ']).filter(Boolean))];

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Box sx={navStyle}>
        <Button component={Link} to="/dashboard" variant="outlined" size="small">Commandes</Button>
        <Button variant="contained" size="small" disabled>Plats</Button>
        <Button component={Link} to="/dashboard/menus" variant="outlined" size="small">Menus</Button>
        <Button component={Link} to="/dashboard/categories" variant="outlined" size="small">Catégories</Button>
        <Button component={Link} to="/dashboard/reglages" variant="outlined" size="small">Fermeture / Vacances</Button>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" color="error" onClick={() => { sessionStorage.removeItem('user'); navigate('/dashboard'); }}>
          Déconnexion
        </Button>
      </Box>

      <Typography variant="h4" gutterBottom>Modifier les plats</Typography>

      {message && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      <Tabs
        value={selectedCat}
        onChange={(_, v) => setSelectedCat(v)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ mb: 2 }}
      >
        {CATEGORIES.map(cat => <Tab key={cat} label={cat} value={cat} />)}
      </Tabs>

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ width: 70 }}>Réf</TableCell>
            <TableCell sx={{ width: 250 }}>Nom</TableCell>
            <TableCell sx={{ width: 80 }}>Prix (€)</TableCell>
            <TableCell sx={{ width: 160 }}>Sous-catégorie</TableCell>
            <TableCell>Description</TableCell>
            <TableCell sx={{ width: 120 }}>Image</TableCell>
            <TableCell sx={{ width: 40 }}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {items.map((item, idx) => (
            <TableRow key={idx} hover>
              <TableCell>
                <TextField
                  value={item.ref || ''}
                  onChange={e => handleFieldChange(idx, 'ref', e.target.value)}
                  variant="standard" size="small" sx={{ width: 60 }}
                />
              </TableCell>
              <TableCell>
                <TextField
                  value={item.nom || ''}
                  onChange={e => handleFieldChange(idx, 'nom', e.target.value)}
                  variant="standard" size="small" fullWidth
                />
              </TableCell>
              <TableCell>
                <TextField
                  value={item.prix ?? ''}
                  onChange={e => handleFieldChange(idx, 'prix', e.target.value)}
                  variant="standard" size="small" type="number" sx={{ width: 70 }}
                  inputProps={{ step: '0.5', min: '0' }}
                />
              </TableCell>
              <TableCell>
                <FormControl variant="standard" size="small" fullWidth>
                  <Select
                    value={item['sous-categ'] || ''}
                    onChange={e => handleFieldChange(idx, 'sous-categ', e.target.value)}
                    displayEmpty
                    renderValue={v => v || <em style={{ color: '#aaa' }}>— aucune —</em>}
                  >
                    <MuiMenuItem value=""><em>— aucune —</em></MuiMenuItem>
                    {subCatOptions.map(s => (
                      <MuiMenuItem key={s} value={s}>{s}</MuiMenuItem>
                    ))}
                    {item['sous-categ'] && !subCatOptions.includes(item['sous-categ']) && (
                      <MuiMenuItem value={item['sous-categ']}>{item['sous-categ']}</MuiMenuItem>
                    )}
                  </Select>
                </FormControl>
              </TableCell>
              <TableCell>
                <TextField
                  value={item.description || ''}
                  onChange={e => handleFieldChange(idx, 'description', e.target.value)}
                  variant="standard" size="small" fullWidth
                />
              </TableCell>
              <TableCell>
                <TextField
                  value={item.image || ''}
                  onChange={e => handleFieldChange(idx, 'image', e.target.value)}
                  variant="standard" size="small" sx={{ width: 110 }}
                />
              </TableCell>
              <TableCell>
                <IconButton size="small" color="error" onClick={() => handleDelete(idx)}>
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Box sx={{ mt: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
        <Button variant="outlined" onClick={handleAdd}>+ Ajouter un item</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Sauvegarder'}
        </Button>
        <BackupManager user={user} onRestore={reloadCarte} />
      </Box>
    </Box>
  );
};

export default DashboardPlats;
